import type {
  EditorLayer,
  TileReference,
  UploadedImageTransformSnapshot,
} from '../types/levelEditor';
import { isColorCell, parseTileKey } from '../stores/editorStoreHelpers';
import { UPLOADED_IMAGE_SHEET_ID, UPLOADED_IMAGE_TILESET_ID } from './levelDesignsDeserialize';

const tryCompactUploadedImageLayer = (
  layer: EditorLayer,
  tileSize: number,
): UploadedImageTransformSnapshot | null => {
  const entries = Object.entries(layer.tiles);
  if (entries.length === 0) {
    return null;
  }

  let imagePath: string | null = null;
  let sourceTileSize: number | null = null;
  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  const coordinateAndTile: Array<{ x: number; y: number; tile: TileReference }> = [];
  for (const [key, tile] of entries) {
    if (isColorCell(tile)) {
      return null;
    }
    if (tile.tilesetId !== UPLOADED_IMAGE_TILESET_ID || tile.sheetId !== UPLOADED_IMAGE_SHEET_ID) {
      return null;
    }
    if (imagePath === null) {
      imagePath = tile.imagePath;
    } else if (imagePath !== tile.imagePath) {
      return null;
    }
    if (sourceTileSize === null) {
      sourceTileSize = tile.tileSize;
    } else if (sourceTileSize !== tile.tileSize) {
      return null;
    }
    let coordinates: { x: number; y: number };
    try {
      coordinates = parseTileKey(key);
    } catch {
      return null;
    }
    coordinateAndTile.push({ ...coordinates, tile });
    minX = Math.min(minX, coordinates.x);
    minY = Math.min(minY, coordinates.y);
    maxX = Math.max(maxX, coordinates.x);
    maxY = Math.max(maxY, coordinates.y);
  }

  if (!imagePath || !sourceTileSize || !Number.isFinite(minX) || !Number.isFinite(minY)) {
    return null;
  }

  const columns = maxX - minX + 1;
  const rows = maxY - minY + 1;
  if (columns <= 0 || rows <= 0 || coordinateAndTile.length !== columns * rows) {
    return null;
  }

  for (const entry of coordinateAndTile) {
    const relativeX = entry.x - minX;
    const relativeY = entry.y - minY;
    const expectedSourceX = relativeX * sourceTileSize;
    const expectedSourceY = relativeY * sourceTileSize;
    if (entry.tile.sourceX !== expectedSourceX || entry.tile.sourceY !== expectedSourceY) {
      return null;
    }
  }

  return {
    imagePath,
    imageWidth: Math.max(1, Math.round(columns * sourceTileSize)),
    imageHeight: Math.max(1, Math.round(rows * sourceTileSize)),
    offsetX: Math.floor(minX),
    offsetY: Math.floor(minY),
    scalePercent: (tileSize / sourceTileSize) * 100,
    avoidCropping: true,
  };
};

export const resolveCompactUploadedImageLayer = (
  layer: EditorLayer,
  tileSize: number,
): UploadedImageTransformSnapshot | null => {
  const inferred = tryCompactUploadedImageLayer(layer, tileSize);
  if (!inferred) {
    return null;
  }

  const exact = layer.uploadedImageTransform;
  if (!exact) {
    return inferred;
  }

  const samePlacement =
    exact.imagePath === inferred.imagePath &&
    Math.floor(exact.offsetX) === inferred.offsetX &&
    Math.floor(exact.offsetY) === inferred.offsetY &&
    Math.abs(exact.scalePercent - inferred.scalePercent) < 0.001 &&
    exact.avoidCropping === inferred.avoidCropping;

  if (!samePlacement) {
    return inferred;
  }

  return {
    ...inferred,
    imageWidth: Math.max(1, Math.round(exact.imageWidth)),
    imageHeight: Math.max(1, Math.round(exact.imageHeight)),
  };
};
